import React, { PropsWithChildren, ReactNode, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { SVG, Icons } from '../../../components/atoms/SVG';
import bem from '../../../lib/bemUtils/bem';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { SearchFormResetButton } from './SearchFormResetButton';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { SectionTypes } from '../../../types/api/Sections';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';

export enum SearchFormModifier {
  PAGE_LIST = 'page-list',
}

type SearchFormProps = {
  idPrefix: string;
  modifier?: Modifier<SearchFormModifier>;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  fqPathId?: string;
  hasUnsupportedSonSearchFeatures?: boolean;
  searchFormResults?: ReactNode;
  initialQueryParameter?: string;
};

export function SearchFormBase({
  idPrefix,
  modifier,
  required,
  minLength,
  maxLength,
  fqPathId,
  hasUnsupportedSonSearchFeatures,
  searchFormResults,
  initialQueryParameter,
  children,
}: PropsWithChildren<SearchFormProps>) {
  const { t } = useSonTranslation('SearchForm');
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [focus, setFocus] = useState(false);
  const [queryValue, setQueryValue] = useState<string>(searchParams.get('q') || initialQueryParameter || '');
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const inputId = `${idPrefix}-search-${useHtmlValidId()}`;

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const params = new URLSearchParams(searchParams);
    params.delete('page');

    if (queryValue) {
      params.set('q', queryValue);
    } else {
      params.delete('q');
    }

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
        hash: inputId,
      },
      {
        replace: true,
        state: {
          refetch: 'force',
          omitSection: [SectionTypes.personal, SectionTypes.navigation, SectionTypes.breadcrumb, SectionTypes.footer],
        },
      }
    );
  };

  const resetInput = (event: React.FormEvent) => {
    event.preventDefault();
    setQueryValue('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className={bem('search-form', modifier)}>
      <form
        action={location.pathname}
        onSubmit={onSubmit}
        role="search"
        className="search-form__form"
        ref={formRef}
        method="GET"
      >
        {fqPathId && <input type="hidden" name="fq_pathId" value={fqPathId} />}
        <div className="search-form__inner-container">
          <div className="search-form__padded-container">
            <p className="search-form__form-container">
              <span className="search-form__input-container">
                <input
                  ref={inputRef}
                  id={inputId}
                  className="search-form__input font-regular-13-13"
                  type="search"
                  required={required}
                  value={queryValue}
                  name="q"
                  minLength={minLength}
                  maxLength={maxLength}
                  placeholder={t('search.title')}
                  title={t('search.title')}
                  aria-label={t('search.label')}
                  autoComplete="off"
                  spellCheck="false"
                  aria-describedby="search-results-text"
                  onChange={event => setQueryValue(event.target.value)}
                  onFocus={() => setFocus(true)}
                  onBlur={() => setFocus(false)}
                />
                <SearchFormResetButton onClick={resetInput} focus={focus} hasInputValue={!!queryValue} />
              </span>
              <button type="submit" className="search-form__button font-medium-15-15">
                <span className="search-form__text-container">{t('search.search')}</span>
                <span className="search-form__icon-container">
                  <SVG svgSpriteId={Icons.SEARCH_MAGNIFIER} svgClass="search-form__icon-search" />
                </span>
              </button>
            </p>
            {searchFormResults}
            {hasUnsupportedSonSearchFeatures ? (
              <p className="search-form__warning font-regular-15-21">{t('search.unsupportedSonSearchFeatures')}</p>
            ) : null}
          </div>
        </div>
        {children}
      </form>
    </div>
  );
}
